import {
  GetShiftsByEventCommandOutput,
  Shift,
  ShiftTimingType,
  ShiftType,
} from '@amzn/red-velvet-api';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { MIN_PER_HOUR, SHORT_SHIFT_HOURS } from '../../utils/constants';

export function calculateShiftTime(shift: Shift | undefined, vcCreated?: boolean) {
  if (!shift) return 0;

  if (vcCreated) {
    // For flexible shifts, use the explicit shiftLength (per-session duration)
    if (
      shift.shiftTimingType === ShiftTimingType.FLEXIBLE &&
      shift.shiftLength != null &&
      shift.shiftLength > 0
    )
      return shift.shiftLength;
  }

  // DROP_IN shifts on all events have shiftLength=0 by design;
  // return 0 so the coordinator must enter time manually rather than
  // pre-filling with the window duration (which is meaningless for drop-ins)
  if (
    shift.shiftType === ShiftType.DROP_IN &&
    (shift.shiftLength == null || shift.shiftLength === 0)
  )
    return 0;

  const startDate = shift.startDateTime ? new Date(shift.startDateTime) : null;
  const endDate = shift.endDateTime ? new Date(shift.endDateTime) : null;
  if (startDate && endDate) {
    return (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  }
  return 0;
}

export function useShiftTime(eventId: string, shiftId: string, vcCreated?: boolean) {
  // We need the shift information to determine the default length of time for attendance
  const shiftsQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getShiftsByEvent', { eventId }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });

  const shift = (shiftsQuery.data || []).find((shift: Shift) => shift.shiftId === shiftId);
  return calculateShiftTime(shift, vcCreated);
}

export function getGuardedShiftLength(shift: Shift | undefined, vcCreated?: boolean) {
  const shiftLength = calculateShiftTime(shift, vcCreated);
  // Don't pre-fill for shifts > 8 hours so coordinators enter time manually.
  return shiftLength > SHORT_SHIFT_HOURS * MIN_PER_HOUR ? 0 : shiftLength;
}
